import clsx from "clsx";
import { CSSProperties, FC } from "react";

import { DateYMDString, ICoursesCalendarSlotDto } from "@ryde/shared";

import { CoursesCalendarCellExpandPopper } from "./courses-calendar-cell.expand-popper";
import { CoursesCalendarCellFooter } from "./courses-calendar-cell.footer";
import { CoursesCalendarCellSlotItem } from "./courses-calendar-cell.slot-item";
import { getCoursesCalendarCellOverflowCount } from "./courses-calendar-cell.utils";
import { useCoursesCalendarCell } from "./use-courses-calendar-cell";
import { useCoursesCalendarCellExpand } from "./use-courses-calendar-cell-expand";

interface ICoursesCalendarTableBodyCellProps {
  style: CSSProperties;
  dateKey: DateYMDString;
  hourIndex: number;
  slotMap: Map<string, ICoursesCalendarSlotDto[]>;
  onCreateSlotRide: (slot: ICoursesCalendarSlotDto) => Promise<number>;
  onAfterSlotProduce?: () => void;
}

export const CoursesCalendarTableBodyCell: FC<
  ICoursesCalendarTableBodyCellProps
> = ({
  style,
  dateKey,
  hourIndex,
  slotMap,
  onCreateSlotRide,
  onAfterSlotProduce,
}) => {
  const expandState = useCoursesCalendarCellExpand();
  const {
    cellId,
    list,
    selectedInCellCount,
    isAllInCellSelected,
    hasSlotSelectionInCell,
    isFullHourRowSelected,
    isSlotSelected,
    toggleSlotSelected,
    setAllInCellSelected,
    courseOpenClassName,
    onOpenCourseClickHandler,
    onCreateRideClickHandler,
    openRideEdit,
  } = useCoursesCalendarCell({
    dateKey,
    hourIndex,
    slotMap,
    onCreateSlotRide,
    onAfterSlotProduce,
  });

  const hasOverflow = getCoursesCalendarCellOverflowCount(list.length) > 0;
  const showFooter = list.length > 0;

  return (
    <div
      data-e2e="courses_calendar_cell"
      data-e2e-id={cellId}
      className={clsx("cc-cal-cell booking_cell thin_scrollbar", {
        cc_cal_cell__has_slot_selection: hasSlotSelectionInCell,
        cc_cal_cell__has_overflow: hasOverflow,
        cc_cal_cell__expand_open: expandState.isExpandOpen,
        cc_cal_cell__row_tint: isFullHourRowSelected,
      })}
      style={style}
    >
      <div className="calendar_cell-dto_list">
        {list.map(slot => (
          <CoursesCalendarCellSlotItem
            key={slot.id}
            slot={slot}
            isSelected={isSlotSelected(slot.id)}
            onToggleSelected={toggleSlotSelected}
            courseOpenClassName={courseOpenClassName}
            onOpenCourseClick={onOpenCourseClickHandler}
            onCreateRideClick={onCreateRideClickHandler}
            onRideOpen={openRideEdit}
          />
        ))}
      </div>
      {showFooter && (
        <CoursesCalendarCellFooter
          totalCount={list.length}
          selectedCount={selectedInCellCount}
          isAllSelected={isAllInCellSelected}
          hasSlotSelection={hasSlotSelectionInCell}
          hasOverflow={hasOverflow}
          isExpandOpen={expandState.isExpandOpen}
          tooltipsDisabled={false}
          expandAnchorRef={expandState.expandAnchorRef}
          onToggleSelectAll={() => setAllInCellSelected(!isAllInCellSelected)}
          onToggleExpand={expandState.toggleExpandOpen}
        />
      )}
      {hasOverflow && (
        <CoursesCalendarCellExpandPopper
          expandState={expandState}
          dateKey={dateKey}
          hourIndex={hourIndex}
          slots={list}
          selectedInCellCount={selectedInCellCount}
          isAllInCellSelected={isAllInCellSelected}
          isSlotSelected={isSlotSelected}
          toggleSlotSelected={toggleSlotSelected}
          setAllInCellSelected={setAllInCellSelected}
          courseOpenClassName={courseOpenClassName}
          onOpenCourseClick={onOpenCourseClickHandler}
          onCreateRideClick={onCreateRideClickHandler}
          onRideOpen={openRideEdit}
        />
      )}
    </div>
  );
};
